import { first } from 'lodash';
import shopService from 'modules/shop/services';
import { IProductCategoryItem, IProductItem } from 'modules/shop/types/product';
import { IStaffItem } from 'modules/shop/types/staff';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { ISagaFunc } from 'services/actionConfigs';
import { IResponseData } from 'services/response';
import apis from './apis';
import { actions } from './slice';
import { ICashDepositItem } from './types/deposit';
import { INewTicketResData } from './types/ticket';
import { inputSearchRef } from '../Cashier/Products/InputSearch';
import { cashierCtrl } from '../Cashier/cashier.controller';
import { IBillInfoResData } from './types/bill';
import cashierApis from './apis';
import { setPageLoading } from 'widgets/PageLoading';

const lstBillOnHold = function* () {
  try {
    const res: IResponseData<INewTicketResData> = yield call(apis.lstBillOnHold);
    if (res.data.data) {
      yield put(actions.async.lstBillOnHold.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.async.lstBillOnHold.fail({}));
  }
};
const products: ISagaFunc<string> = function* ({ payload }) {
  try {
    const res: IResponseData<IProductItem[]> = yield call(apis.products, payload);
    if (res.data.data) {
      yield put(actions.async.products.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.async.products.fail({}));
  }
};

const initProduct = function* () {
  const state: IProductCategoryItem[] = yield select(shopService.selectors.getCategories);
  const firstCategory = first(state);
  if (!firstCategory) return null;
  yield put(actions.async.products.fetch(firstCategory.id));
};

const searchProducts: ISagaFunc<string> = function* ({ payload: textSearch }) {
  if (!textSearch.trim()) return yield put(actions.async.searchProducts.fail({}));
  yield delay(200);
  try {
    const res: IResponseData<IProductItem[]> = yield call(apis.searchProducts, textSearch);
    if (res.data.data) {
      yield put(actions.async.searchProducts.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.async.searchProducts.fail({}));
  }
};

const getCardsByTransactionId: ISagaFunc<string> = function* ({ payload: billId }) {
  yield delay(200);
  try {
    const res: IResponseData<IProductItem[]> = yield call(apis.getListMultipleCards, billId);
    if (res.data.data) {
      yield put(actions.async.getCardsByTransactionId.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.async.getCardsByTransactionId.fail({}));
  }
};

const getDetailBill: ISagaFunc<string> = function* ({ payload: billId }) {
  yield delay(200);
  try {
    const res: IResponseData<IProductItem[]> = yield call(apis.getBillById, billId);
    if (res.data.data) {
      yield put(actions.async.getDetailBill.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.async.getDetailBill.fail({}));
  }
};

const getDetailBillPreview: ISagaFunc<string> = function* ({ payload: billId }) {
  yield delay(200);
  try {
    const res: IResponseData<IProductItem[]> = yield call(apis.getBillById, billId);
    if (res.data.data) {
      yield put(actions.async.getDetailBillPreview.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.async.getDetailBillPreview.fail({}));
  }
};

const getStaffWorking = function* () {
  yield delay(200);
  try {
    const res: IResponseData<IStaffItem[]> = yield call(apis.getStaffWorking);
    if (res.data.data) {
      yield put(actions.async.getStaffWorking.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.async.getStaffWorking.fail({}));
  }
};

const getCashDeposit = function* () {
  yield delay(200);
  try {
    const res: IResponseData<ICashDepositItem[]> = yield call(apis.cashDeposit);
    if (res.data.data) {
      yield put(actions.async.cashDeposit.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.async.cashDeposit.fail({}));
  }
};

const cashierInit = function* () {
  yield delay(200);
  document.removeEventListener('keyup', () => undefined);
  document.addEventListener('keyup', function (event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      inputSearchRef.current?.focus();
    }
  });
  document.querySelectorAll('button').forEach(btn => {
    btn.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    });
  });
  cashierCtrl.generateNewTicket();
  setTimeout(() => {
    inputSearchRef.current?.focus();
  }, 1500);

  yield all([
    put(actions.async.lstBillOnHold.fetch()),
    put(actions.async.cashDeposit.fetch()),
    put(actions.async.promotions.fetch()),
  ]);
};

const syncDetailData: ISagaFunc<string> = function* ({ payload: billId }) {
  setPageLoading(true);
  try {
    const res: IResponseData<IBillInfoResData> = yield call(cashierApis.getBillById, billId);
    if (res.data.data) {
      cashierCtrl.switchTicketWithDetailData(res.data.data);
      yield delay(100);
      yield put(actions.setActiveItemLatest(''));
    }
  } catch (error) { } finally {
    setPageLoading(false);
  }
};

const getCustomerDetail: ISagaFunc<string> = function* ({ payload }) {
  try {
    const res: IResponseData<any> = yield call(cashierApis.getCustomerDetail, payload);
    if (res.data.data) {
      yield put(actions.async.getCustomerDetail.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.async.getCustomerDetail.fail({}));
  }
};

const getPromotions = function* () {
  yield delay(200);
  try {
    const res: IResponseData<IProductItem[]> = yield call(cashierApis.getPromotions);
    if (res.data.data) {
      yield put(actions.async.promotions.success(res.data.data));
    }
  } catch (error) {
  }
};

const onSetActiveItemLatest = function* () {
  yield cashierCtrl.calculateCashier();
  yield delay(100);
  yield cashierCtrl.syncDraftBill();
};

export default function* sagas() {
  yield takeLatest(actions.init, cashierInit);
  yield takeLatest(actions.async.lstBillOnHold.fetch, lstBillOnHold);
  yield takeLatest(actions.async.promotions.fetch, getPromotions);
  yield takeLatest(actions.async.promotions.fetchPaging, getPromotions);
  yield takeLatest(actions.async.products.fetch, products);
  yield takeLatest(actions.async.searchProducts.fetch, searchProducts);
  yield takeLatest(actions.async.getCardsByTransactionId.fetch, getCardsByTransactionId);
  yield takeLatest(actions.async.getDetailBill.fetch, getDetailBill);
  yield takeLatest(actions.async.getDetailBillPreview.fetch, getDetailBillPreview);
  yield takeLatest(actions.async.getStaffWorking.fetch, getStaffWorking);
  yield takeLatest(actions.async.cashDeposit.fetch, getCashDeposit);
  yield takeLatest(actions.async.getCustomerDetail.fetch, getCustomerDetail);
  yield takeLatest(actions.setActiveItemLatest, onSetActiveItemLatest);
  yield takeLatest(shopService.actions.async.getCategories.success, initProduct);
  yield takeLatest(actions.syncDetailData, syncDetailData);
}